import { useMemo } from "react";

import useTranslations from "@/shared/lang/useTranslations";

import {
  LEDGER_ACCOUNT_HINT_KEYS,
  LEDGER_ACCOUNT_LABEL_KEYS,
  LEDGER_ACCOUNT_TYPE_LABEL_KEYS,
  LEDGER_ENTRY_FALLBACK_KEYS,
  LEDGER_ENTRY_LABEL_KEYS,
  LEDGER_ENTRY_LABEL_PREFIXES,
  LEDGER_TAX_ACCOUNT_PREFIXES,
} from "../constants/paymentsLedger.constants";
import {
  LedgerDirection,
  type LedgerAccount,
  type LedgerEntryGroup,
  type LedgerOperationKind,
} from "../types/paymentsLedger.types";

export interface LedgerLabels {
  /** Translated account name; falls back to the backend `name` for a code outside the mirrored catalog. */
  account: (account: LedgerAccount) => string;
  /** One-line explanation of what the account holds, or null when the code is not in the catalog. */
  accountHint: (account: LedgerAccount) => string | null;
  /** False when the account code is not in the mirrored catalog — the UI flags it instead of hiding it. */
  isKnownAccount: (account: LedgerAccount) => boolean;
  accountType: (account: LedgerAccount) => string;
  /**
   * Who the account belongs to: the client organization by name, "global" for platform-wide accounts, or a
   * neutral "provider account" label. The provider CODE is never returned (rule 12).
   */
  accountScope: (account: LedgerAccount) => string;
  /** Entry label: reversal first, then the known descriptions, then the raw description, then its type. */
  entry: (entry: LedgerEntryGroup) => string;
  direction: (direction: LedgerDirection) => string;
  operationKind: (kind: LedgerOperationKind) => string;
}

/**
 * Every translated string the ledger renders, in one place.
 *
 * Two things it deliberately does NOT do: invent a label for an account code or a description the backend has
 * not declared (both fall back to the raw value, flagged where it matters), and expose provider identity — a
 * provider-scoped account is described by its role, never by its `scope_provider_code`.
 */
function taxAccountOf(code: string) {
  const rule = LEDGER_TAX_ACCOUNT_PREFIXES.find((candidate) => code.startsWith(candidate.prefix));
  if (!rule) return null;
  const [country, tax] = code.slice(rule.prefix.length).split("-");
  return tax ? { rule, label: `${tax} ${country}` } : null;
}

export const useLedgerLabels = (organizationNames: Record<string, string>): LedgerLabels => {
  const { t } = useTranslations("paymentsLedger");

  return useMemo<LedgerLabels>(() => {
    const isKnownAccount = (account: LedgerAccount) =>
      LEDGER_ACCOUNT_LABEL_KEYS[account.code] !== undefined || taxAccountOf(account.code) !== null;

    return {
      isKnownAccount,
      account: (account) => {
        const key = LEDGER_ACCOUNT_LABEL_KEYS[account.code];
        if (key) return t(key);
        const tax = taxAccountOf(account.code);
        return tax ? t(tax.rule.label, { tax: tax.label }) : account.name;
      },
      accountHint: (account) => {
        const key = LEDGER_ACCOUNT_HINT_KEYS[account.code] ?? taxAccountOf(account.code)?.rule.hint;
        return key ? t(key) : null;
      },
      accountType: (account) => {
        const key = LEDGER_ACCOUNT_TYPE_LABEL_KEYS[account.type];
        return key ? t(key) : account.type;
      },
      accountScope: (account) => {
        if (account.scope_organization_id) {
          const name = organizationNames[account.scope_organization_id];
          return name ?? `${t("scope_organization")} · ${account.scope_organization_id.slice(0, 8)}`;
        }
        // Presence of a provider scope is meaningful; its value is not renderable (rule 12).
        if (account.scope_provider_code) return t("scope_provider");
        return t("scope_global");
      },
      entry: (entry) => {
        if (entry.reversesEntryId) return t("entry_reversal");
        const description = entry.description?.trim();
        if (description) {
          const exact = LEDGER_ENTRY_LABEL_KEYS[description];
          if (exact) return t(exact);
          const prefixed = LEDGER_ENTRY_LABEL_PREFIXES.find((rule) => description.startsWith(rule.prefix));
          if (prefixed) return t(prefixed.key);
          return description;
        }
        return t(LEDGER_ENTRY_FALLBACK_KEYS[entry.operationType]);
      },
      direction: (direction) =>
        direction === LedgerDirection.DEBIT ? t("direction_debit") : t("direction_credit"),
      operationKind: (kind) =>
        kind === "payouts"
          ? t("picker_tab_payouts")
          : kind === "payins"
            ? t("picker_tab_payins")
            : t("picker_tab_swaps"),
    };
  }, [organizationNames, t]);
};
